import { Auth, Hub } from 'aws-amplify';

import { CustomAuthEvent } from './authEvent';

export const customAuthChannel = 'customAuth';

/**
 * Put an event on our custom channel.
 *
 * @param event the event to place on the custom channel
 */
async function dispatchCustomAuthEvent(event: CustomAuthEvent): Promise<void> {
  Hub.dispatch(customAuthChannel, { event });
}

export async function signup({
  email,
  password,
  phone_number,
}: {
  email: string;
  password: string;
  phone_number: string;
}): Promise<ReturnType<typeof Auth.signUp>> {
  try {
    const response = await Auth.signUp({
      username: phone_number,
      password,
      attributes: { email, phone_number: phone_number },
    });
    return response;
  } catch (err) {
    console.log('error...: ', err);
    throw err;
  }
}

/**
 * Sign the user out with respect to Cognito.
 *
 * This does *not* clear Apollo cache, so should not be used in isolation.
 */
export async function signout(): Promise<void> {
  try {
    await Auth.signOut();
  } catch (err) {
    console.log('sign out err', err);
  }
}

export async function signin(
  phone_number: string,
  password: string,
): Promise<void> {
  try {
    await Auth.signIn(phone_number, password);
  } catch (err) {
    console.log('error...: ', err);
    throw err;
  }
}

export async function confirmSignup(
  code: string,
  username: string,
): Promise<void> {
  try {
    await Auth.confirmSignUp(username, code);

    //  Dispatch the event explicitly because Amplify does not broadcast for confirm
    await dispatchCustomAuthEvent(CustomAuthEvent.SIGNUP_CONFIRM);
  } catch (err) {
    console.log('error...: ', err);
    throw err;
  }
}

/**
 * Send email verification code to the current user.
 */
export async function sendEmailVerificationCode(): Promise<void> {
  await Auth.verifyCurrentUserAttribute('email');
}

/**
 * Confirm the email with Cognito by using the verification code that was send to it.
 *
 * @param code - Code the user has submitted to verify the email
 */
export async function confirmEmail(code: string): Promise<void> {
  try {
    await Auth.verifyCurrentUserAttributeSubmit('email', code);
  } catch (err) {
    console.log(err.message);
    throw new Error(err.message);
  }

  //  Dispatch the event explicitly because Amplify does not broadcast for confirm
  await dispatchCustomAuthEvent(CustomAuthEvent.EMAIL_CONFIRM);
}

export async function resendSignupCode(username: string): Promise<void> {
  await Auth.resendSignUp(username);
}

export async function sendMeForgotPasswordCode(
  username: string,
): Promise<void> {
  try {
    await Auth.forgotPassword(username);
  } catch (err) {
    console.log(err);
    throw err;
  }
}

/**
 * Change the password for a non authenticated user by using a phone code to confirm identity.
 * @param username
 * @param code
 * @param newPassword
 */
export async function confirmForgotPasswordCode(
  username: string,
  code: string,
  newPassword: string,
): Promise<void> {
  try {
    await Auth.forgotPasswordSubmit(username, code, newPassword);
  } catch (err) {
    console.log(err);
    throw err;
  }
}

/**
 * Reset password for an authenticated user who knows the existing one.
 * @param oldPassword
 * @param newPassword
 */
export async function resetPassword(
  oldPassword: string,
  newPassword: string,
): Promise<void> {
  try {
    const user = await Auth.currentAuthenticatedUser();
    await Auth.changePassword(user, oldPassword, newPassword);
  } catch (err) {
    console.log(err);
    throw err;
  }
}
